import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect } from 'react';
import useTabs from 'src/components/10-atoms/tabs/useTabs';
import usePersistedState from 'src/hooks/globalState/usePersistedState';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import StorageKeys from 'src/lib/storage/storageKeys';
import syncBoxManager from 'src/lib/sync-box/SyncBoxManager';
import useIsSuperAdmin from 'src/modules/authentication/hooks/useIsSuperAdmin';
import useUserStorage from 'src/modules/authentication/storage/user/hook';
import { useRPPendingCountQuery } from 'src/modules/review-pipeline/graphql/pendingCount';
import { SupportedRPEntityTypes } from 'src/modules/review-pipeline/graphql/utils';
import useI18n from 'src/modules/translations/hooks/useI18n';
import isNot from 'src/utils/array/isNot';
import { useExamLevelDetailsQuery } from '../../graphql/exam-level/getDetails';
import { getNodeUserSyncBox } from '../../offline/sync-boxes/nodeUser';
export default function useData() {
    const navigation = useNavigation();
    const route = useRoute();
    const [selectedExamLevel, setSelectedExamLevel] = usePersistedState(StorageKeys.epSelectedExamLevel);
    const { translate } = useI18n();
    const [examLevelDetailsQueryState, examLevelDetailsQueryActions] = useExamLevelDetailsQuery({
        lazy: true,
    });
    useEffect(() => {
        if (route.params?.examLevelID) {
            if (route.params?.examLevelID !== selectedExamLevel?.examLevel?.id) {
                examLevelDetailsQueryActions.fetch({
                    examLevelID: route.params.examLevelID,
                });
                return;
            }
        }
        else if (selectedExamLevel?.examLevel?.id) {
            navigation.setParams({ examLevelID: selectedExamLevel.examLevel.id });
        }
    }, []);
    useEffect(() => {
        if (examLevelDetailsQueryState.data) {
            const examLevel = examLevelDetailsQueryState.data.ep_examLevelDetails;
            if (examLevel.id !== selectedExamLevel?.examLevel?.id) {
                setSelectedExamLevel({
                    community: examLevel.ancestorsDetails.community,
                    examLevel,
                });
            }
        }
    }, [examLevelDetailsQueryState.data]);
    const handleExamLevelChange = useStableCallback((newValue) => {
        navigation.setParams({ examLevelID: newValue.examLevel?.id });
        setSelectedExamLevel(newValue);
    });
    const [tabsState, tabsActions] = useTabs();
    useEffectWithCtx(ctx => {
        if (selectedExamLevel?.examLevel) {
            const config = selectedExamLevel?.examLevel.config;
            const shouldShowWritten = config?.EXAM_LEVEL?.written?.enable ?? true;
            const shouldShowOrals = config?.EXAM_LEVEL?.orals?.enable ?? false;
            ctx.tabsActions.setTabs([
                shouldShowWritten && {
                    id: 'WRITTEN',
                    label: translate('Written'),
                },
                shouldShowOrals && {
                    id: 'ORALS',
                    label: translate('Orals'),
                },
            ].filter(isNot(false)));
            ctx.tabsActions.setActiveIndex(0);
        }
    }, { tabsActions }, [selectedExamLevel]);
    const isSuperAdmin = useIsSuperAdmin();
    const [reviewPendingCountQueryState, reviewPendingCountQueryActions] = useRPPendingCountQuery({
        skip: !isSuperAdmin,
        variables: {
            types: SupportedRPEntityTypes,
        },
        pollInterval: 5 * 60000,
    });
    const [userDetails] = useUserStorage();
    useEffect(() => {
        if (userDetails?.id) {
            // Create syncBox for qna_node_user collection
            const qnaNodeUserSyncBox = getNodeUserSyncBox();
            syncBoxManager.register(qnaNodeUserSyncBox);
        }
    }, [userDetails?.id]);
    const state = {
        isSuperAdmin,
        reviewPendingCountQuery: reviewPendingCountQueryState,
        selectedExamLevel,
        tabs: tabsState,
    };
    const actions = {
        reviewPendingCountQuery: reviewPendingCountQueryActions,
        handleExamLevelChange,
        tabs: tabsActions,
    };
    return [state, actions];
}
